
import { defineComponent, PropType, reactive, ref } from "vue";
import { getSamplingPointImages } from "@/service/image.service";
import SamplingPointImageComponent from "./SamplingPointImage.component.vue";
import { Image } from "@/types/Image.type";
import ObserverComponent from "../Observer.component.vue";
import { getColourById } from "@/service/colour.service";
import { Estimation } from "@/types/estimation.type";
import { removeDuplicate } from "@/utils/estimation.util";
import securityService from "@/service/security.service";
import { AppRole } from "@/types/app.type";
import { ElMessage } from "element-plus";
import NoDataComponent from "@/components/NoData.component.vue";
import { getDatesWithEstimationsGallery } from "@/service/estimation.service";

interface ImageColour extends Image {
  colourCode?: number;
}
export default defineComponent({
  components: {
    ObserverComponent,
    SamplingPointImageComponent,
    NoDataComponent,
  },
  props: {
    samplingPoint: {
      type: Object,
      required: false,
      immediate: true,
      deep: true,
    },
    title: {
      type: String,
      required: false,
    },
    id: {
      type: String,
      required: true,
    },
    image: {
      type: Array as PropType<ImageData[]>,
      required: false,
    },
  },

  setup() {
    const isAppDemo = securityService.hasRole(AppRole.DEMOS) ? true : false;

    const paramsVisible = ref(true);
    const toggleVisibility = () => {
      paramsVisible.value = !paramsVisible.value;
    };
    return {
      isAppDemo,
      paramsVisible,
      toggleVisibility,
    };
  },
  data() {
    return {
      currentPage: 1,
      images: [] as ImageColour[],
      hasMore: true,
      colourCode: undefined as number | undefined,
      fromDate: "",
      toDate: "",
      isSearching: false,
      datesWithData: reactive<string[]>([]),
      dateKey: 0,
      cola: 0,
    };
  },
  methods: {
    async getMarkedDates() {
      try {
        const response1 = await getDatesWithEstimationsGallery([parseInt(this.id)]);
        const dates = response1.map((data: any) => {
          const date = new Date(data.image_date);
          return date.toDateString();
        });
        this.datesWithData.splice(0, this.datesWithData.length, ...dates);
        this.dateKey++;
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    },

    hasEstimatedData(day: Date) {
      return this.datesWithData.includes(day.toDateString());
    },

    close() {
      this.$emit("close");
    },
    getSamplingPointName() {
      if (this.images.length > 0 && this.images[0].samplingPoint) {
        return this.isAppDemo ? this.images[0].samplingPoint.id : this.images[0].samplingPoint.name;
      }
      return "";
    },

    refreshDatePickers() {
      this.fromDate = "";
      this.toDate = "";
      this.isSearching = false;
      this.currentPage = 1;
      this.dateKey = 0;
      this.images = [];
      this.hasMore = true;
      this.cola = 0;
      this.getSamplingPointName();
    },

    async showMore() {
      // - get result for current page
      this.cola++;
      if (!this.isSearching && this.cola == 1) {
        const rowPerPage = 10;

        const { data, totalRows, currentPage } = await getSamplingPointImages(
          this.id,
          this.currentPage,
          rowPerPage
        );

        this.currentPage = currentPage;
        // - concat result array
        this.images = [...this.images, ...data];

        for (const image of this.images) {
          const imageEstimation = removeDuplicate(image.imageEstimation as Estimation[]);
          if (imageEstimation && typeof image.id === "number") {
            // Verificar que image.id sea un número
            for (const est of imageEstimation) {
              const object = est;
              if (object instanceof Object) {
                const estimation = object as Estimation;
                if (
                  estimation.parameter?.name === "Color" &&
                  estimation.value &&
                  image.samplingPoint?.id
                ) {
                  const colour = await getColourById(estimation.value, image.samplingPoint?.id);
                  image.colourCode = colour.code;
                }
              }
            }
          }
        }

        // - check if exist more result
        if (totalRows <= this.currentPage * rowPerPage) {
          this.hasMore = false;
        }

        this.currentPage++;
        this.cola = 0;

        if (this.images.length < rowPerPage) {
          this.showMore();
        }
      }
    },

    async showMoreSearch() {
      if (this.fromDate && this.toDate) {
        const rowPerPage = 10;

        const { data, totalRows } = await getSamplingPointImages(
          this.id,
          this.currentPage,
          rowPerPage,
          new Date(this.fromDate).toISOString(),
          new Date(this.toDate).toISOString()
        );

        //Comprobamos que los datos que llegan son mayores que 0 y que no son undefined para que no haya errores
        if (data !== undefined && data.length > 0) {
          this.currentPage++;
          this.images = [...this.images, ...data];
        }

        this.isSearching = true;

        if (totalRows <= (this.currentPage - 1) * rowPerPage) {
          this.hasMore = false;
        }

        if (this.images.length < rowPerPage && data !== undefined) {
          this.showMoreSearch();
        }
      }
    },

    async searchImages() {
      if (this.fromDate && this.toDate) {
        const rowPerPage = 10;
        try {
          const { data, totalRows } = await getSamplingPointImages(
            this.id,
            1, // Reiniciamos la página a 1 al realizar una nueva búsqueda
            rowPerPage,
            new Date(this.fromDate).toISOString(),
            new Date(this.toDate).toISOString()
          );

          this.images = [];

          this.isSearching = true; // Ponemos que estamos buscando imagenes
          this.images = [...data]; // Reemplaza las imágenes existentes con las de la búsqueda

          this.currentPage = 2; // Actualiza la página actual

          if (totalRows <= rowPerPage) {
            this.hasMore = false;
          } else {
            this.hasMore = true;
            this.showMoreSearch();
          }
        } catch (error) {
          this.hasMore = false;
          ElMessage.warning(this.$t("No hay imágenes para este rango seleccionado"));
        }
      }
    },
  },

  mounted() {
    this.getMarkedDates();
  },
});
